import { type LangKey, type PillStatus, texts } from "@sveabank/cc-common";
import type { FullApplicationData } from "../../types/application";
import { stepIs } from "../../utils/application";

type ApplicationItemStatus =
	| "agreementsToSign"
	| "beneficialOwnerDeclined"
	| "otherSign"
	| "manualHandling"
	| "finalizeApplication"
	| "ongoing";

type ApplicationStatusContent = {
	pillText: LangKey;
	pillStatus: PillStatus;
	description: LangKey | null;
};

export const getApplicationItemStatus = (
	currentUserSsn: string,
	application: FullApplicationData,
): ApplicationStatusContent => {
	const {
		details: {
			applicant: { nationalId: applicantSsn },
		},
		status,
		steps,
	} = application;

	const isExternalSigner = applicantSsn !== currentUserSsn;
	const signPackageAgreementStep = steps.find(stepIs("SignPackageAgreementStep"));
	const signGuarantorAgreementStep = steps.find(stepIs("SignGuarantorAgreementStep"));
	const finalizeApplicationStep = steps.find(stepIs("FinalizeStep"));

	const beneficialOwnerDeclined =
		steps.find(stepIs("ConfirmBeneficialOwnershipStep"))?.state.status === "Declined";

	const guarantorSignAvailable =
		signGuarantorAgreementStep?.status === "NotCompleted" &&
		signGuarantorAgreementStep?.state?.signers.some(
			(s) => s.nationalId === currentUserSsn && !s.isSigned,
		);

	const guarantorSignOtherAvailable =
		signGuarantorAgreementStep?.status === "NotCompleted" &&
		signGuarantorAgreementStep?.state?.signers.some(
			(s) => s.nationalId !== currentUserSsn && !s.isSigned,
		);

	const packageAgreementAvailable =
		signPackageAgreementStep?.status === "NotCompleted" &&
		signPackageAgreementStep?.state?.signers.some(
			(s) => s.nationalId === currentUserSsn && !s.isSigned,
		);

	const packageAgreementOtherAvailable =
		signPackageAgreementStep?.status === "NotCompleted" &&
		signPackageAgreementStep?.state?.signers.some(
			(s) => s.nationalId !== currentUserSsn && !s.isSigned,
		);

	const signAvailable = guarantorSignAvailable || packageAgreementAvailable;

	const signOtherAvailable = guarantorSignOtherAvailable || packageAgreementOtherAvailable;

	if (signAvailable) {
		return applicationItemStatuses.agreementsToSign;
	}
	if (beneficialOwnerDeclined) {
		return applicationItemStatuses.beneficialOwnerDeclined;
	}
	if (signOtherAvailable) {
		return applicationItemStatuses.otherSign;
	}
	if (status === "OrderUnderReview") {
		return applicationItemStatuses.manualHandling;
	}
	if (
		!isExternalSigner &&
		!!finalizeApplicationStep &&
		finalizeApplicationStep.status !== "Completed"
	) {
		return applicationItemStatuses.finalizeApplication;
	}
	return applicationItemStatuses.ongoing;
};

const applicationItemStatuses: Record<ApplicationItemStatus, ApplicationStatusContent> = {
	ongoing: {
		pillText: texts.dashboardPage.applications.statuses.ongoing,
		description: null,
		pillStatus: "pending",
	},
	beneficialOwnerDeclined: {
		pillText: texts.dashboardPage.applications.statuses.ongoing,
		description: texts.dashboardPage.applications.statusDescriptions.beneficialOwnerDeclined,
		pillStatus: "pending",
	},
	manualHandling: {
		pillText: texts.dashboardPage.applications.statuses.finalized,
		description: texts.dashboardPage.applications.statusDescriptions.manualHandling,
		pillStatus: "success",
	},
	otherSign: {
		pillText: texts.dashboardPage.applications.statuses.ongoing,
		description: texts.dashboardPage.applications.statusDescriptions.signOther,
		pillStatus: "pending",
	},
	agreementsToSign: {
		pillText: texts.dashboardPage.applications.statuses.ongoing,
		description: texts.dashboardPage.applications.statusDescriptions.agreementsToSign,
		pillStatus: "pending",
	},

	finalizeApplication: {
		pillText: texts.dashboardPage.applications.statuses.ongoing,
		description: texts.dashboardPage.applications.statusDescriptions.readyForFinalize,
		pillStatus: "pending",
	},
};
